import { $T } from 'client/library/translation'
import type { FrameMenuItem } from 'app/types'
import type { ServiceInitArgs } from 'app/services/base'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useShortcutManagersStore } from 'app/services/pinia/shortcut-managers-store'
import { isActionEnabled } from 'compositions/commands'
import { useSnowdance } from '@xmindltd/snowmonster'

interface Point {
  x: number
  y: number
}

export function buildMapMenu(services: ServiceInitArgs, location: Point) {
  const { action } = services
  if (!action) {
    return []
  }

  const workbook = useWorkspaceStore().workbook
  if (!workbook) return []

  const shortcutManager = useShortcutManagersStore().getShortcutManager('sharing-editor')

  const pasteAction = workbook.getAction('paste')
  const floatingTopicAction = workbook.getAction('editor.addFloatingTopicByDragging')
  const addRelationshipAction = workbook.getAction('editor.addRelationship')
  const addBoundaryAction = workbook.getAction('editor.addBoundary')
  const focusCenterAction = workbook.getAction('editor.focusAndSelectCenter')
  // const fitMapAction = workbook.getAction('editor.fitMap')
  const toggleAllAction = workbook.getAction('editor.toggleAllBranch')

  const toggleAllLabel = isActionEnabled('mutate:foldAllDescendantBranches') ? $T('Fold All Sub-Branches') : $T('Unfold All Sub-Branches')

  const menuItems: FrameMenuItem[] = [
    {
      label: $T('Paste'),
      shortcut: shortcutManager?.findDescriptionByAction(pasteAction),
      enable: pasteAction.isEnabled,
      action: () => pasteAction.execute({ payload: { targetPoint: location } }),
    },
    {
      type: 'separator',
    },
    {
      label: $T('Insert Floating Topic'),
      shortcut: shortcutManager?.findDescriptionByAction(floatingTopicAction),
      enable: floatingTopicAction.isEnabled,
      action: () => floatingTopicAction.execute(),
    },
    {
      label: $T('Insert Relationship'),
      shortcut: shortcutManager?.findDescriptionByAction(addRelationshipAction),
      enable: addRelationshipAction.isEnabled,
      action: () => addRelationshipAction.execute(),
    },
    {
      label: $T('Insert Boundary'),
      shortcut: shortcutManager?.findDescriptionByAction(addBoundaryAction),
      enable: addBoundaryAction.isEnabled,
      action: () => addBoundaryAction.execute(),
    },
    {
      type: 'separator',
    },
    // {
    //   label: $T('Fit Map'),
    //   shortcut: shortcutManager?.findDescriptionByAction(fitMapAction),
    //   enable: fitMapAction.isEnabled,
    //   action: () => fitMapAction.execute(),
    // },
    {
      label: toggleAllLabel,
      shortcut: shortcutManager?.findDescriptionByAction(toggleAllAction),
      enable: toggleAllAction.isEnabled,
      action: () => toggleAllAction.execute(),
    },
    {
      type: 'separator',
    },
    {
      label: useSnowdance().getShouldShowGuidelineForMovingTopics() ? $T('Hide Smart Guideline') : $T('Display Smart Guideline'),
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.toggleSmartGuideline')),
      enable: workbook.getAction('editor.focusAndSelectCenter').isEnabled,
      action: () => {
        workbook.getAction('editor.toggleSmartGuideline').execute()
      },
    },
    {
      type: 'separator',
    },
    {
      label: $T('Go to Central Topic'),
      shortcut: shortcutManager?.findDescriptionByAction(focusCenterAction),
      enable: focusCenterAction.isEnabled,
      action: () => focusCenterAction.execute(),
    },
    {
      label: $T('Select All'),
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('selectAll')),
      enable: workbook.getAction('selectAll').isEnabled,
      action: () => {
        workbook.getAction('selectAll').execute()
      },
    },
  ]

  return menuItems
}
